/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createSelector, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { CarouselTemplate } from "../../model/enums";
// Imported from the concrete modules rather than the `model/navigation`
// barrel. This slice is in the root reducer, so it is initialized whenever the
// store is — including in suites that stub the barrel down to the one or two
// helpers they care about, where a barrel import would resolve to `undefined`
// at module-init time and take the whole store down with it.
import { LocationUrls } from "../../model/navigation/LocationUrls";
import {
  GridEdgeBehaviour,
  moveGridFocus
} from "../../model/navigation/GridNavigation";
import type { TGridDirection } from "../../model/navigation/GridNavigation";
import type { TCarouselItem } from "../../model/types";
import type { UnityRootState } from "../UnityAppStore";
import type {
  SeeMoreState,
  TSeeMoreCursorPage,
  TSeeMorePreviewVariant,
  TSeeMoreRequest,
  TSeeMoreSource
} from "./seeMore.types";
import { SeeMoreScreen } from "./seeMore.types";

const initialState: SeeMoreState = {
  isOpen: false,
  title: "",
  template: CarouselTemplate.POSTER_HORIZONTAL,
  items: [],
  totalCount: 0,
  pageCursor: null,
  source: null,
  requestedColumns: 0,
  previewVariant: "content",
  returnUrl: LocationUrls.HOME_SCREEN,
  focusedIndex: 0,
  columns: 1,
  isLoadingMore: false
};

/**
 * See More — the full-page grid a carousel's "See More" tile opens.
 *
 * Screen-agnostic by construction: a caller pushes a {@link TSeeMoreRequest}
 * through `openSeeMore` and the screen renders whatever is in it. Nothing in
 * this slice knows about My Hub, or about any other caller, so a second screen
 * needs no changes here to reuse the page.
 *
 * Focus lives here rather than in the screen for the same reason it does on
 * every other screen in the app: `SeeMoreScreenController` receives `dispatch`
 * and no component handle, so D-pad moves have to be expressible as actions.
 */
const seeMoreSlice = createSlice({
  name: "seeMore",
  initialState,
  reducers: {
    /**
     * Opens the page with a caller's payload, replacing whatever was there.
     *
     * `columns` stays at its initial 1 until the view reports the resolved
     * count, which it does on mount. One is the safe placeholder: with a
     * single column the grid arithmetic degenerates to a flat list, so a D-pad
     * move that somehow arrives first can only move by one cell, never onto a
     * cell that does not exist.
     */
    openSeeMore(state: SeeMoreState, action: PayloadAction<TSeeMoreRequest>) {
      const request: TSeeMoreRequest = action.payload;
      state.isOpen = true;
      state.title = request.title;
      state.template = request.template;
      state.items = request.items;
      state.totalCount =
        request.totalCount === undefined ? 0 : request.totalCount;
      state.pageCursor =
        request.pageCursor === undefined || request.pageCursor === ""
          ? null
          : request.pageCursor;
      state.source = request.source === undefined ? null : request.source;
      state.requestedColumns =
        request.columns === undefined ? 0 : request.columns;
      state.previewVariant =
        request.previewVariant === undefined
          ? "content"
          : request.previewVariant;
      state.returnUrl = request.returnUrl;
      state.focusedIndex = 0;
      state.columns = 1;
      state.isLoadingMore = false;
    },

    /** Clears the page. Dispatched when the screen unmounts. */
    closeSeeMore(): SeeMoreState {
      return initialState;
    },

    /**
     * Persists the page state for backward stickiness on re-entry.
     *
     * Preserves pagination data, focus indices, and items so the user returns
     * to the same scroll position and focused cell. Only clears transient
     * loading state so the component starts clean on next mount.
     *
     * Dispatched when the screen unmounts and focus was in the grid.
     * If the user navigates to a different caller's See More, `openSeeMore`
     * will replace this state with the new request.
     */
    persistSeeMoreState(state: SeeMoreState): SeeMoreState {
      return {
        ...state,
        isLoadingMore: false
      };
    },

    /**
     * Records the column count the grid resolved to.
     *
     * Focus is re-clamped because a column change re-flows every row: the cell
     * that was last on screen may no longer exist.
     */
    setSeeMoreColumns(state: SeeMoreState, action: PayloadAction<number>) {
      state.columns = Math.max(action.payload, 1);
      state.focusedIndex = clampToItems(state, state.focusedIndex);
    },

    /** Moves focus to a specific cell — the pointer/hover path. */
    focusSeeMoreItem(state: SeeMoreState, action: PayloadAction<number>) {
      state.focusedIndex = clampToItems(state, action.payload);
    },

    /**
     * Moves focus one cell in `direction` — the D-pad path.
     *
     * Edges clamp rather than wrap or exit: the grid is the only focusable
     * region on the page, so there is nowhere for focus to go and running off
     * the top should not eject the user from the screen. BACK is how you
     * leave.
     */
    navigateSeeMoreFocus(
      state: SeeMoreState,
      action: PayloadAction<TGridDirection>
    ) {
      const next: number | null = moveGridFocus(
        state.focusedIndex,
        action.payload,
        {
          itemCount: state.items.length,
          columns: state.columns,
          horizontalEdge: GridEdgeBehaviour.CLAMP,
          verticalEdge: GridEdgeBehaviour.CLAMP
        }
      );
      if (next !== null) {
        state.focusedIndex = next;
      }
    },

    /** Marks a page of additional items as in flight, or no longer so. */
    setSeeMoreLoadingMore(state: SeeMoreState, action: PayloadAction<boolean>) {
      state.isLoadingMore = action.payload;
    },

    /**
     * Appends a fetched page of items.
     *
     * Appending rather than replacing keeps the focused index meaningful: the
     * cell the user is on has the same flat index before and after, so the
     * grid does not jump under them when a page lands.
     */
    appendSeeMoreItems(
      state: SeeMoreState,
      action: PayloadAction<TCarouselItem[]>
    ) {
      if (action.payload.length === 0) {
        state.isLoadingMore = false;
        return;
      }
      state.items = state.items.concat(action.payload);
      state.isLoadingMore = false;
    },

    /** Appends a cursor page and atomically replaces its continuation cursor. */
    appendSeeMoreCursorPage(
      state: SeeMoreState,
      action: PayloadAction<TSeeMoreCursorPage>
    ) {
      state.items = state.items.concat(action.payload.items);
      state.pageCursor = action.payload.pageCursor;
      state.isLoadingMore = false;
    }
  }
});

/** Clamps `index` into the item range, or 0 when there are no items. */
const clampToItems = (state: SeeMoreState, index: number): number => {
  if (state.items.length === 0) {
    return 0;
  }
  if (index < 0) {
    return 0;
  }
  return Math.min(index, state.items.length - 1);
};

export const {
  openSeeMore,
  closeSeeMore,
  persistSeeMoreState,
  setSeeMoreColumns,
  focusSeeMoreItem,
  navigateSeeMoreFocus,
  setSeeMoreLoadingMore,
  appendSeeMoreItems,
  appendSeeMoreCursorPage
} = seeMoreSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────

export const selectSeeMoreIsOpen = (state: UnityRootState): boolean =>
  state.seeMore.isOpen;

export const selectSeeMoreTitle = (state: UnityRootState): string =>
  state.seeMore.title;

export const selectSeeMoreTemplate = (
  state: UnityRootState
): CarouselTemplate => state.seeMore.template;

export const selectSeeMoreItems = (state: UnityRootState): TCarouselItem[] =>
  state.seeMore.items;

export const selectSeeMoreFocusedIndex = (state: UnityRootState): number =>
  state.seeMore.focusedIndex;

export const selectSeeMoreColumns = (state: UnityRootState): number =>
  state.seeMore.columns;

export const selectSeeMoreRequestedColumns = (state: UnityRootState): number =>
  state.seeMore.requestedColumns;

export const selectSeeMorePreviewVariant = (
  state: UnityRootState
): TSeeMorePreviewVariant => state.seeMore.previewVariant;

export const selectSeeMoreTotalCount = (state: UnityRootState): number =>
  state.seeMore.totalCount;

export const selectSeeMorePageCursor = (state: UnityRootState): string | null =>
  state.seeMore.pageCursor;

export const selectSeeMoreSource = (
  state: UnityRootState
): TSeeMoreSource | null => state.seeMore.source;

export const selectSeeMoreReturnUrl = (state: UnityRootState): string =>
  state.seeMore.returnUrl;

export const selectSeeMoreIsLoadingMore = (state: UnityRootState): boolean =>
  state.seeMore.isLoadingMore;

/**
 * True when the collection is known to be longer than what is laid out.
 *
 * A caller that omits `totalCount` is saying "this is everything", so the
 * absence of a count reads as "nothing more to fetch" rather than "unknown".
 */
export const selectSeeMoreHasMore: (state: UnityRootState) => boolean =
  createSelector(
    [
      selectSeeMoreSource,
      selectSeeMoreTotalCount,
      selectSeeMoreItems,
      selectSeeMorePageCursor
    ],
    (
      source: TSeeMoreSource | null,
      totalCount: number,
      items: TCarouselItem[],
      pageCursor: string | null
    ): boolean =>
      source !== null && source.screen === SeeMoreScreen.SEARCH_SCREEN
        ? pageCursor !== null && pageCursor.length > 0
        : totalCount > items.length
  );

/** The focused item, or null when the page is empty. */
export const selectSeeMoreFocusedItem: (
  state: UnityRootState
) => TCarouselItem | null = createSelector(
  [selectSeeMoreItems, selectSeeMoreFocusedIndex],
  (items: TCarouselItem[], focusedIndex: number): TCarouselItem | null => {
    const item: TCarouselItem | undefined = items[focusedIndex];
    return item === undefined ? null : item;
  }
);

export default seeMoreSlice.reducer;
